import React, { useState, useEffect } from 'react';
import { IconClipboardList, IconTrophy, IconMoodSad, IconGolf } from '@tabler/icons-react';
import api from '../api/api';

/**
 * 레거시(php) sign 로직 재현
 * 골프장 + holeType 단위로 score 그룹핑 → best/worst 추출
 */
function buildData(scores, memberMap) {
  const groups = {};
  scores.forEach(s => {
    const holeType = s.sectionCount >= 2 ? '9' : '18';
    const key = `${s.golfCourseId}_${holeType}`;
    if (!groups[key]) {
      groups[key] = {
        golfCourseId: s.golfCourseId, courseName: s.courseName,
        holeType, totalHoles: s.totalHoles, scoreMap: {},
      };
    }
    if (!groups[key].scoreMap[s.score]) groups[key].scoreMap[s.score] = [];
    // 같은 유저 중복은 첫 기록(최초 달성일)만 유지
    if (!groups[key].scoreMap[s.score].find(x => x.userId === s.userId)) {
      groups[key].scoreMap[s.score].push({
        userId: s.userId, score: s.score, date: s.reservedDate,
        name: memberMap[s.userId]?.nickname || memberMap[s.userId]?.name || `#${s.userId}`,
        color: memberMap[s.userId]?.color,
      });
    }
  });

  // 인원별 best/worst
  const perUser = {};
  // 골프장별 best/worst
  const perCourse = [];

  Object.values(groups).sort((a, b) => a.courseName.localeCompare(b.courseName)).forEach(g => {
    const sortedScores = Object.keys(g.scoreMap).map(Number).sort((a, b) => a - b);
    if (sortedScores.length === 0) return;

    const holeLabel = `${g.totalHoles || 18}홀`;
    const bestScore = sortedScores[0];
    const bestUsers = g.scoreMap[bestScore];
    const isBestTie = bestUsers.length >= 2;

    let worstScore = null, worstUsers = [], isWorstTie = false;
    if (sortedScores.length > 1) {
      worstScore = sortedScores[sortedScores.length - 1];
      worstUsers = g.scoreMap[worstScore];
      isWorstTie = worstUsers.length >= 2;
    }

    // 인원별
    bestUsers.forEach(u => {
      if (!perUser[u.userId]) perUser[u.userId] = { best: [], worst: [] };
      perUser[u.userId].best.push({ courseName: g.courseName, holeLabel, score: bestScore, tie: isBestTie, date: u.date });
    });
    if (worstScore !== null) {
      worstUsers.forEach(u => {
        if (!perUser[u.userId]) perUser[u.userId] = { best: [], worst: [] };
        perUser[u.userId].worst.push({ courseName: g.courseName, holeLabel, score: worstScore, tie: isWorstTie, date: u.date });
      });
    }

    // 골프장별
    perCourse.push({
      courseName: g.courseName, holeLabel, golfCourseId: g.golfCourseId,
      best: { score: bestScore, users: bestUsers, tie: isBestTie },
      worst: worstScore !== null ? { score: worstScore, users: worstUsers, tie: isWorstTie } : null,
    });
  });

  return { perUser, perCourse };
}

export default function SignboardPage({ me, showToast }) {
  const [members, setMembers] = useState([]);
  const [perUser, setPerUser] = useState({});
  const [perCourse, setPerCourse] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const [mRes, sRes] = await Promise.all([
          api.get('/golf/members'),
          api.get('/golf/sign-scores'),
        ]);
        const mList = mRes.data || [];
        setMembers(mList);
        const mMap = {};
        mList.forEach(m => { mMap[m.id] = m; });
        const { perUser, perCourse } = buildData(sRes.data || [], mMap);
        setPerUser(perUser);
        setPerCourse(perCourse);
      } catch {}
      finally { setLoading(false); }
    };
    load();
  }, []);

  if (loading) {
    return <div className="text-center py-5"><div className="spinner-border text-primary" /></div>;
  }

  return (
    <div>
      {/* ── 인원별 통계 ── */}
      <div className="d-flex align-items-center gap-2 mb-3">
        <IconClipboardList size={22} />
        <h3 className="mb-0" style={{ fontWeight: 700 }}>인원별 통계</h3>
      </div>

      <div className="row g-3 row-cols-1 row-cols-md-2 row-cols-lg-3 row-cols-xl-5 mb-4">
        {members.map(m => {
          const data = perUser[m.id] || { best: [], worst: [] };
          const bestSorted = [...data.best].sort((a, b) => a.courseName.localeCompare(b.courseName));
          const worstSorted = [...data.worst].sort((a, b) => a.courseName.localeCompare(b.courseName));

          return (
            <div key={m.id} className="col">
              <div className="card h-100">
                <div className="card-header py-3" style={{ borderLeft: `4px solid ${m.color || 'var(--tblr-primary)'}` }}>
                  <div className="d-flex align-items-center gap-3">
                    <span style={{
                      width: 44, height: 44, borderRadius: '50%',
                      background: m.bgcolor || m.color || 'var(--tblr-primary)',
                      color: m.bgcolor ? (m.color || '#fff') : '#fff',
                      display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                      fontSize: 18, fontWeight: 700, flexShrink: 0,
                    }}>
                      {(m.nickname || m.name)?.[0]?.toUpperCase() || '?'}
                    </span>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontWeight: 700, fontSize: 16, color: m.color || undefined }}>
                        {m.nickname || m.name}
                      </div>
                      {m.nickname && m.name !== m.nickname && (
                        <div className="text-muted" style={{ fontSize: 12 }}>{m.name}</div>
                      )}
                    </div>
                  </div>
                </div>
                <div className="card-body py-2 px-3">
                  <div className="row g-0">
                    <div className="col-6" style={{ borderRight: '1px solid var(--tblr-border-color, #eee)' }}>
                      <div className="d-flex align-items-center gap-1 mb-2 px-1">
                        <IconTrophy size={14} color="#f59e0b" />
                        <span style={{ fontSize: 12, fontWeight: 700, color: '#206bc4' }}>베스트 {bestSorted.length}</span>
                      </div>
                      {bestSorted.length === 0 ? (
                        <div className="text-muted text-center py-2" style={{ fontSize: 12 }}>-</div>
                      ) : (
                        <div style={{ fontSize: 12, lineHeight: 1.8 }}>
                          {bestSorted.map((s, i) => (
                            <div key={i} className="px-1">
                              {s.tie && <span style={{ color: '#d63939', fontWeight: 700 }}>(공)</span>}
                              {s.courseName}
                              <span className="text-green" style={{ fontSize: 11 }}>({s.holeLabel})</span>
                              -<span style={{ fontWeight: 700, color: s.tie ? '#d63939' : '#206bc4' }}>{s.score}</span>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                    <div className="col-6">
                      <div className="d-flex align-items-center gap-1 mb-2 px-2">
                        <IconMoodSad size={14} color="#f76707" />
                        <span style={{ fontSize: 12, fontWeight: 700, color: '#f76707' }}>워스트 {worstSorted.length}</span>
                      </div>
                      {worstSorted.length === 0 ? (
                        <div className="text-muted text-center py-2" style={{ fontSize: 12 }}>-</div>
                      ) : (
                        <div style={{ fontSize: 12, lineHeight: 1.8 }}>
                          {worstSorted.map((s, i) => (
                            <div key={i} className="px-2">
                              {s.tie && <span style={{ color: '#f76707', fontWeight: 700 }}>(공)</span>}
                              {s.courseName}
                              <span className="text-green" style={{ fontSize: 11 }}>({s.holeLabel})</span>
                              -<span style={{ fontWeight: 700, color: s.tie ? '#f76707' : '#206bc4' }}>{s.score}</span>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* ── 골프장 목록 ── */}
      <div className="d-flex align-items-center gap-2 mb-3">
        <IconGolf size={22} />
        <h3 className="mb-0" style={{ fontWeight: 700 }}>골프장 목록</h3>
        <span className="badge bg-blue-lt">{perCourse.length}코스</span>
      </div>

      <div className="card">
        <div className="table-responsive">
          <table className="table table-vcenter table-hover card-table" style={{ fontSize: 13 }}>
            <thead>
              <tr>
                <th style={{ width: 220 }}>골프장</th>
                <th className="text-center" style={{ width: 100 }}>
                  <span className="d-inline-flex align-items-center gap-1"><IconTrophy size={13} color="#f59e0b" /> 베스트</span>
                </th>
                <th>베스트 선수</th>
                <th className="text-center" style={{ width: 100 }}>
                  <span className="d-inline-flex align-items-center gap-1"><IconMoodSad size={13} color="#f76707" /> 워스트</span>
                </th>
                <th>워스트 선수</th>
              </tr>
            </thead>
            <tbody>
              {perCourse.map((c, ci) => (
                <tr key={ci}>
                  <td>
                    <span className="fw-medium">{c.courseName}</span>
                    <span className="text-green ms-1" style={{ fontSize: 11 }}>({c.holeLabel})</span>
                  </td>
                  <td className="text-center">
                    <span style={{ fontWeight: 700, fontSize: 15, color: '#206bc4' }}>{c.best.score}</span>
                  </td>
                  <td>
                    <div className="d-flex flex-wrap gap-1">
                      {c.best.users.map((u, ui) => (
                        <span key={ui} style={{
                          fontSize: 12, padding: '2px 8px', borderRadius: 12,
                          background: c.best.tie ? 'rgba(214,57,57,0.1)' : 'rgba(32,107,196,0.1)',
                          color: u.color || (c.best.tie ? '#d63939' : '#206bc4'),
                          fontWeight: 600,
                        }}>
                          {c.best.tie && <span style={{ color: '#d63939' }}>(공) </span>}
                          {u.name}
                          {u.date && <span className="text-muted fw-normal ms-1" style={{ fontSize: 10 }}>{u.date}</span>}
                        </span>
                      ))}
                    </div>
                  </td>
                  <td className="text-center">
                    {c.worst ? (
                      <span style={{ fontWeight: 700, fontSize: 15, color: '#f76707' }}>{c.worst.score}</span>
                    ) : (
                      <span className="text-muted">-</span>
                    )}
                  </td>
                  <td>
                    {c.worst ? (
                      <div className="d-flex flex-wrap gap-1">
                        {c.worst.users.map((u, ui) => (
                          <span key={ui} style={{
                            fontSize: 12, padding: '2px 8px', borderRadius: 12,
                            background: c.worst.tie ? 'rgba(247,103,7,0.1)' : 'rgba(32,107,196,0.1)',
                            color: u.color || (c.worst.tie ? '#f76707' : '#206bc4'),
                            fontWeight: 600,
                          }}>
                            {c.worst.tie && <span style={{ color: '#f76707' }}>(공) </span>}
                            {u.name}
                            {u.date && <span className="text-muted fw-normal ms-1" style={{ fontSize: 10 }}>{u.date}</span>}
                          </span>
                        ))}
                      </div>
                    ) : (
                      <span className="text-muted" style={{ fontSize: 12 }}>-</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
